/**
 * Shared UI primitives for the ClearViolations home page.
 * Encodes the recurring design recipes (HANDOFF §1) so no section repeats the
 * class strings. Tokens: navy / brand / ink / surface (tailwind.config.js).
 */
import { NavLink } from 'react-router-dom';

// Smooth in-page scroll that respects the sticky header height (do NOT use
// scrollIntoView inside the app shell, HANDOFF §5).
const HEADER_OFFSET = 76;
export function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: y, behavior: 'smooth' });
}

/** Section wrapper: consistent vertical rhythm + centered 1200px container. */
export function Section({ id, bg = 'white', className = '', containerClassName = '', children }) {
    const bgMap = {
        white: 'bg-white',
        alt: 'bg-surface-alt',
        navy: 'bg-navy text-white',
    };
    return (
        <section id={id} className={`${bgMap[bg]} py-[72px] ${className}`}>
            <div className={`mx-auto w-full max-w-[1200px] px-6 ${containerClassName}`}>
                {children}
            </div>
        </section>
    );
}

/** Eyebrow, intentionally removed per design preference. Renders nothing so
 *  every section that still calls <Eyebrow> simply shows no kicker. */
export function Eyebrow() {
    return null;
}

/** Two-tone display heading: navy line 1 + orange line 2. */
export function Heading({ line1, line2, onNavy = false, className = '' }) {
    return (
        <h2 className={`font-display text-[32px] font-bold leading-[1.12] tracking-[-0.025em] sm:text-[42px] ${onNavy ? 'text-white' : 'text-navy'} ${className}`}>
            {line1}{line1 && line2 ? ' ' : ''}
            {line2 && <span className="text-brand">{line2}</span>}
        </h2>
    );
}

/**
 * CTA, orange pill by default; on navy backgrounds it flips to white bg + navy
 * text (HANDOFF §5). Handles in-page anchors (#id), tel:/mailto:, and routes.
 */
export function Cta({ label, href = '#', variant = 'solid', onNavy = false, className = '', onClick }) {
    const base = 'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-display font-semibold transition';
    const styles = {
        solid: onNavy
            ? 'bg-white text-navy hover:bg-brand hover:text-white'
            : 'bg-brand text-white hover:bg-navy',
        outline: onNavy
            ? 'border border-white/40 text-white hover:bg-white hover:text-navy'
            : 'border border-navy/20 text-navy hover:bg-navy hover:text-white',
    };
    const cls = `${base} ${styles[variant]} ${className}`;

    const handleAnchor = (e) => {
        if (href && href.startsWith('#')) {
            e.preventDefault();
            scrollToId(href.slice(1));
        }
        onClick?.(e);
    };

    if (href && href.startsWith('#')) {
        return <a href={href} onClick={handleAnchor} className={cls}>{label}</a>;
    }
    if (href && (href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('http'))) {
        return <a href={href} className={cls}>{label}</a>;
    }
    return <NavLink to={href} className={cls} onClick={onClick}>{label}</NavLink>;
}
